import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { FC, useContext, useEffect, useMemo, useRef, useCallback } from "react";
import { useGetVacationBalancesColumnDefs } from "./useGetVacationBalancesColumnDefs";
import { Row } from "react-bootstrap";
import { CustomNoRowsOverlay } from "@hr/statisticsview/vacancy/ManualTab/CustomNoRowsOverlay";
import { localeText } from "@hr/actions/datagrid.js";
import { ManualTabContext } from "@hr/statisticsview/vacancy/ManualTab/ManualTabContext";
import { ManualTabGridRef } from "@hr/statisticsview/vacancy/ManualTab/ManualTab.types";
import { useQueryClient } from "@tanstack/react-query";
import { manualTabTableQueryKey } from "@hr/statisticsview/vacancy/ManualTab/ManualTab.constants";
import { PersonVacationBalanceJSON } from "@hr/api/model";

export const ManualTabTable: FC<{ gridRef: ManualTabGridRef }> = ({ gridRef }) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const columnDefs = useGetVacationBalancesColumnDefs();
  const { setSelectedRows, vacationBalances, editedCellKeys, setEditedCellKeys } = useContext(ManualTabContext);
  const containerRef = useRef<HTMLDivElement>(null);

  const onSelectionChanged = () => {
    const selectedData = gridRef?.current?.api.getSelectedRows();
    setSelectedRows(selectedData ?? []);
  };

  const editCells = (cell: PersonVacationBalanceJSON, colField: string) => {
    const editedCells: PersonVacationBalanceJSON[] =
      queryClient.getQueryData([manualTabTableQueryKey, "StoredData"]) ?? [];
    const changedCells = [...editedCells];
    const editedCellIndex = editedCells?.map(({ id }) => id).indexOf(cell.id);

    if (editedCellIndex > -1) {
      changedCells[editedCellIndex] = cell;
    } else {
      changedCells.push(cell);
    }
    queryClient.setQueryData([manualTabTableQueryKey, "StoredData"], changedCells);

    // Track the edited cell
    const cellKey = `${cell.id}:${colField}`;
    setEditedCellKeys((prev) => new Set(prev).add(cellKey));
  };

  // Cell style callback to highlight edited cells
  const cellStyle = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (params: any) => {
      const cellKey = `${params.data?.id}:${params.colDef?.field}`;
      return { backgroundColor: editedCellKeys.has(cellKey) ? "#C4BAD9" : "" };
    },
    [editedCellKeys],
  );

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    editable: true,
    singleClickEdit: true,
    flex: 1,
    filter: true,
    cellStyle,
  }), [cellStyle]);

  // AG Grid won't pick up defaultColDef changes reactively — push it explicitly before refreshing cell styles
  useEffect(() => {
    const api = gridRef?.current?.api;
    if (!api) return;
    api.updateGridOptions({ defaultColDef });
    api.refreshCells({ force: true });
  }, [editedCellKeys, gridRef, defaultColDef]);

  // Deselect focused cell when user clicks outside the grid container
  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        gridRef?.current?.api?.clearFocusedCell();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [gridRef]);

  return (
    <Row ref={containerRef} className="ag-theme-material styles-ag flex-grow-1 mt-3 p-0 m-0">
      <AgGridReact
        className="p-0 m-0"
        ref={gridRef}
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        rowData={vacationBalances}
        getRowId={(params) => {
          return String(params.data.id);
        }}
        localeText={localeText(i18n)}
        noRowsOverlayComponent={CustomNoRowsOverlay}
        stopEditingWhenCellsLoseFocus
        gridOptions={{
          rowSelection: { mode: "multiRow", checkboxes: true },
        }}
        onCellValueChanged={(event) => editCells(event.data, event.colDef?.field ?? "")}
        onSelectionChanged={onSelectionChanged}
      />
    </Row>
  );
};
